import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import BarcodeScannerCore from './BarcodeScannerCore';

// Free/open-source engine (MIT):
// - @zxing/browser provides the camera + continuous decode loop
// - @zxing/library provides formats/hints
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

const PREFERRED_CAMERA_KEY = 'preferredCameraId';

const chooseBestCamera = (devices) => {
  const scored = devices.map((device) => {
    const label = (device.label || '').toLowerCase();
    let score = 0;

    if (label.includes('ultra') && label.includes('wide') && label.includes('back')) {
      score += 100;
    }

    if (label.includes('back') || label.includes('rear')) score += 5;
    if (label.includes('environment')) score += 4;
    if (!label.includes('wide') && !label.includes('ultra')) score += 3;
    if (label.includes('0')) score += 1;

    return { device, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.length > 0 ? scored[0].device : devices[0];
};

const normalizeVinCandidate = (raw) => {
  if (!raw) return null;

  // VINs are 17 chars, uppercase letters (except I/O/Q) + digits.
  // Strip whitespace and common punctuation that scanners sometimes include.
  const cleaned = String(raw).trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (cleaned.length !== 17) return null;

  // Disallow I, O, Q in VINs (per ISO 3779 convention).
  if (/[IOQ]/.test(cleaned)) return null;

  // Basic sanity: must contain at least 1 digit.
  if (!/\d/.test(cleaned)) return null;

  return cleaned;
};

export default function BarcodeScannerWrapper({ show, onBarcodeDetected, onClose, beepRef }) {
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);
  const videoRef = useRef(null);

  // Reader + controls are kept in refs so we can stop them reliably during cleanup.
  const readerRef = useRef(null);
  const controlsRef = useRef(null);

  const hints = useMemo(() => {
    const h = new Map();

    // VIN barcodes are most commonly Code 39; some vendors use Code 128.
    h.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_39, BarcodeFormat.CODE_128]);

    // Helps in harder lighting / angles. Might cost a little CPU but worth it for VINs.
    h.set(DecodeHintType.TRY_HARDER, true);

    return h;
  }, []);

  // 1) Enumerate cameras and pick a default
  useEffect(() => {
    if (!show) return;

    let mounted = true;

    (async () => {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter(d => d.kind === 'videoinput');

      if (!mounted) return;

      const back = cams.filter(c => !/front|user/i.test(c.label || ''));
      const list = back.length ? back : cams;

      setDevices(list);

      const saved = localStorage.getItem(PREFERRED_CAMERA_KEY);
      const found = list.find(d => d.deviceId === saved);
      const best = found || chooseBestCamera(list) || list[0];

      setDeviceId(best?.deviceId || null);

      if (best?.deviceId) {
        localStorage.setItem(PREFERRED_CAMERA_KEY, best.deviceId);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [show]);

  // 2) Start/stop scanning
  useEffect(() => {
    if (!show || !deviceId || !videoRef.current) return;

    let cancelled = false;

    const stopActiveScan = async () => {
      try {
        controlsRef.current?.stop();
      } catch {
        // ignore
      } finally {
        controlsRef.current = null;
      }

      // Some browsers keep a stream around; ensure tracks are stopped.
      try {
        const stream = videoRef.current?.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach(t => t.stop());
        }
      } catch {
        // ignore
      }

      try {
        if (videoRef.current) videoRef.current.srcObject = null;
      } catch {
        // ignore
      }
    };

    (async () => {
      await stopActiveScan();

      if (cancelled) return;

      // Create reader per session (simpler lifecycle).
      const reader = new BrowserMultiFormatReader(hints, 250);
      readerRef.current = reader;

      try {
        const controls = await reader.decodeFromVideoDevice(
          deviceId,
          videoRef.current,
          (result, error, controls) => {
            if (cancelled) return;

            if (result) {
              const raw = result.getText();
              const vin = normalizeVinCandidate(raw);

              // Ignore non-VIN reads (reduces false positives).
              if (!vin) return;

              beepRef?.current?.play().catch(() => {});

              // Stop immediately to avoid duplicate detections.
              try {
                controls?.stop?.();
              } catch {
                // ignore
              }

              onBarcodeDetected(vin);
              onClose();
            }

            // errors are expected during scanning; ignore to keep loop running
            // console.debug('scan error', error);
          }
        );

        controlsRef.current = controls;
      } catch (e) {
        console.error('Failed to start barcode scanning:', e);
      }
    })();

    return () => {
      cancelled = true;
      stopActiveScan();
      readerRef.current = null;
    };
  }, [show, deviceId, hints, onBarcodeDetected, onClose, beepRef]);

  const handleSelect = (e) => {
    const id = e.target.value;
    setDeviceId(id);
    localStorage.setItem(PREFERRED_CAMERA_KEY, id);
  };

  if (!show || !deviceId) return null;

  return ReactDOM.createPortal(
    <BarcodeScannerCore
      videoRef={videoRef}
      videoDevices={devices}
      selectedDeviceId={deviceId}
      onSelectDevice={handleSelect}
      onClose={() => {
        onClose();
      }}
      beepRef={beepRef}
    />,
    document.getElementById('scanner-portal-root')
  );
}